import { DesktopAppFrame } from "@/components/desktop/DesktopChrome";
import { LiveTrackingMap } from "@/components/map/live-tracking-map";
import { ThemedText } from "@/components/themed-text";
import { BrandColors } from "@/constants/theme";
import { isCustomerRole } from "@/lib/roles";
import { showToast } from "@/lib/toast";
import BottomSheet, { BottomSheetScrollView } from "@gorhom/bottom-sheet";
import Ionicons from "@react-native-vector-icons/ionicons";
import { useFocusEffect, useLocalSearchParams, useRouter } from "expo-router";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  AppState,
  AppStateStatus,
  Linking as ReactLinking,
  ScrollView,
  StyleSheet,
  TouchableOpacity,
  View,
} from "react-native";
import { GestureHandlerRootView } from "react-native-gesture-handler";

import { BackButton } from "@/components/booking/BackButton";
import { BackToHomeButton } from "@/components/booking/BackToHomeButton";
import { BookingHeaderCard } from "@/components/booking/BookingHeaderCard";
import { BookingNotFound } from "@/components/booking/BookingNotFound";
import { BookingPayment } from "@/components/booking/BookingPayment";
import { BookingVehicleDetailsLink } from "@/components/booking/BookingVehicleDetailsLink";
import { CustomerInfoCard } from "@/components/booking/CustomerInfoCard";
import { FloatingAddressCard } from "@/components/booking/FloatingAddressCard";
import { CancelButton } from "@/components/cancel-button";
import { Loader } from "@/components/ui/loader";
import { useAuth } from "@/contexts/auth-context";
import {
  syncBookingFromWebSocket,
  useBookingTrack,
} from "@/hooks/queries/use-bookings-query";
import { useDesktopLayout } from "@/hooks/use-desktop-layout";
import { showAlert } from "@/lib/alert";
import { queryClient } from "@/lib/query-client";
import { Booking } from "@/lib/services/bookings";
import { recalculateBookingETA } from "@/lib/utils/map-utils";
import { isLocationOnlyService } from "@/lib/validation/booking";
import { useAuthStore } from "@/stores/use-auth-store";
import { useLocationStore } from "@/stores/use-location-store";
import { desktopStyles } from "@/styles/live-booking";
import { useSafeAreaInsets } from "react-native-safe-area-context";

interface DistanceTravel {
  duration: number;
  distance: number;
}

const pickupStatuses = ["accepted", "arrived", "pickup_inspection"];

export default function LiveTrackingRoute() {
  return (
    <DesktopAppFrame>
      <LiveTrackingScreen />
    </DesktopAppFrame>
  );
}

function LiveTrackingScreen() {
  const { theme } = useAuth();
  const { id } = useLocalSearchParams<{ id: string }>();
  const bookingId = Number(id);
  const hasBookingId = Number.isInteger(bookingId) && bookingId > 0;
  const mapRef = useRef<any>(null);
  const router = useRouter();
  const driverCoordinate = useLocationStore((state) => state.driverCoordinate);
  const { user, setActiveBooking } = useAuthStore();
  const insets = useSafeAreaInsets();
  const desktop = useDesktopLayout();
  const { data, isLoading: loading, refetch } = useBookingTrack(bookingId);
  const freshBooking = data?.booking;
  const booking = freshBooking ?? null;

  const [routeTravel, setRouteTravel] = useState<DistanceTravel | null>(null);

  // Bottom Sheet
  const bottomSheetRef = useRef<BottomSheet>(null);
  const appStateRef = useRef(AppState.currentState);
  const snapPoints = useMemo(() => ["15%", "40%"], []);

  useFocusEffect(
    useCallback(() => {
      if (!hasBookingId) {
        return;
      }
      setActiveBooking(bookingId);
      void refetch();
    }, [bookingId, hasBookingId, setActiveBooking, refetch]),
  );

  useEffect(() => {
    const handleAppStateChange = (nextState: AppStateStatus) => {
      if (
        hasBookingId &&
        appStateRef.current.match(/inactive|background/) &&
        nextState === "active"
      ) {
        void refetch();
      }
      appStateRef.current = nextState;
    };

    const subscription = AppState.addEventListener(
      "change",
      handleAppStateChange,
    );

    return () => {
      subscription.remove();
    };
  }, [hasBookingId, refetch]);

  const origin = useMemo(() => {
    if (!booking) return null;
    const status = booking.status === "pending" || booking.status === "quoted";
    const latitude = status ? Number(booking.from.latitude): Number(booking.driver_latitude);
    const longitude = status ? Number(booking.from.longitude) : Number(booking.driver_longitude);
    if (Number.isNaN(latitude) || Number.isNaN(longitude)) return null;
    return { latitude, longitude };
  }, [booking]);

  const destination = useMemo(() => {
    if (!booking) return null;
    if (isLocationOnlyService(booking.service_type)) {
      return booking.from;
    }
    if (pickupStatuses.includes(booking.status)) {
      return booking.from;
    }
    return booking.to;
  }, [booking]);

  const estimatedTravel = useMemo(() => {
    if (!driverCoordinate || !destination) return null;
    return recalculateBookingETA(driverCoordinate, destination);
  }, [driverCoordinate, destination]);

  const distanceTravelled = routeTravel ?? estimatedTravel;

  const applyBookingUpdate = (updatedBooking: Booking) => {
    syncBookingFromWebSocket(queryClient, updatedBooking);
  };

  const handleCallDriver = () => {
    if (booking?.driver?.phone) {
      ReactLinking.openURL(`tel:${booking.driver.phone}`);
    }
  };

  const handleOpenChat = () => {
    router.push({
      pathname: "/bookings/[id]/chat",
      params: {
        id: Number(id),
        user: JSON.stringify(booking?.driver),
        reload: "false",
      },
    });
  };

  const handleRateBooking = () => {
    showAlert(
      "Rate Booking",
      "Thank you for your booking! Please rate your experience.",
      [
        { text: "No", style: "cancel" },
        {
          text: "Yes, Rate",
          onPress: async () => {
            try {
              router.push(`/rate-booking/${id}`);
            } catch (error: any) {
              showToast.error(
                error.response?.data?.message || "Failed to complete booking",
              );
            }
          },
        },
      ],
    );
  };

  if (loading) {
    return <Loader />;
  }

  if (!booking) {
    return <BookingNotFound />;
  }

  const sheetContent = (
    <>
      {/* Header Section */}
      <BookingHeaderCard
        scheduledAt={booking.scheduled_at}
        serviceTypeLabel={booking.service_type_label}
        statusLabel={booking.status_label}
        status={booking.status}
        paymentMethod={booking.payment_method}
        distanceTravelled={distanceTravelled}
        userRole={user?.role}
        showNavigation={false}
      />

      {/* Actions Section */}
      <View style={styles.actionsSection}>
        <BookingPayment
          booking={booking}
          user={user}
          onSuccess={(updatedBooking: Booking) =>
            applyBookingUpdate(updatedBooking)
          }
        />

        {booking.status === "completed" && booking?.user_rating && (
          <BackToHomeButton />
        )}

        {booking.status === "completed" && booking?.user_rating == null && (
          <TouchableOpacity
            style={[styles.actionButton, styles.completeButton]}
            onPress={handleRateBooking}
          >
            <Ionicons name="checkmark-circle" size={20} color="#fff" />
            <ThemedText style={styles.actionButtonText} type="defaultSemiBold">
              Rate Booking
            </ThemedText>
          </TouchableOpacity>
        )}

        {booking.status === "cancelled" && <BackToHomeButton />}

        {isCustomerRole(user?.role) &&
          (booking.status === "pending" || booking.status === "quoted") && (
            <TouchableOpacity
              style={[styles.actionButton, styles.completeButton]}
              onPress={() => router.push(`/bookings/${id}/search-driver`)}
            >
              <Ionicons name="search" size={20} color="#fff" />
              <ThemedText
                style={styles.actionButtonText}
                type="defaultSemiBold"
              >
                Reach more drivers
              </ThemedText>
            </TouchableOpacity>
          )}
        {isCustomerRole(user?.role) &&
          (booking.status === "pending" ||
            booking.status === "quoted" ||
            booking.status === "accepted") && (
            <CancelButton id={id as string} serviceType={booking.service_type} />
          )}
      </View>

      {/* Driver Info */}
      {isCustomerRole(user?.role) && booking?.driver && (
        <CustomerInfoCard
          name={booking.driver.name}
          roleLabel="Driver"
          subtitle="Driver / Operator"
          variant="detail"
          avatar={booking.driver.avatar}
          rating={booking.driver.reputation_score}
          loyaltyTier={booking.driver.loyalty_tier}
          isVerified={booking.driver.is_top_rated}
          onChat={handleOpenChat}
          onCall={handleCallDriver}
          showActions={
            booking?.payment &&
            (booking?.payment?.status === "authorized" ||
              booking?.payment?.status === "captured")
              ? true
              : false
          }
        />
      )}

      <BookingVehicleDetailsLink booking={booking} />
    </>
  );

  return (
    <GestureHandlerRootView style={styles.container}>
      {/* Map */}
      <View style={styles.mapContainer}>
        {origin && destination && (
          <LiveTrackingMap
            mapRef={mapRef}
            pickupLocation={origin}
            dropoffLocation={destination}
            onDistanceTravelled={(travel) => {
              setRouteTravel(travel);
            }}
            showUserLocation={false}
          />
        )}
        {/* Back Button */}
        <BackButton />

        {/* Floating Addresses */}
        {desktop ? (
          <View pointerEvents="box-none" style={desktopStyles.desktopAddressWrap}>
            <FloatingAddressCard
              fromAddress={booking.from_address}
              toAddress={booking.to_address}
              topInset={0}
              serviceType={booking.service_type}
              style={desktopStyles.desktopAddressCard}
            />
          </View>
        ) : (
          <FloatingAddressCard
            fromAddress={booking.from_address}
            toAddress={booking.to_address}
            topInset={insets.top}
            serviceType={booking.service_type}
          />
        )}
      </View>

      {desktop ? (
        <View pointerEvents="box-none" style={desktopStyles.desktopPanelWrap}>
          <View
            style={[
              desktopStyles.desktopPanel,
              { backgroundColor: theme.background, borderColor: theme.border },
            ]}
          >
            <ScrollView contentContainerStyle={desktopStyles.desktopPanelContent}>
              {sheetContent}
            </ScrollView>
          </View>
        </View>
      ) : (
        <BottomSheet
          ref={bottomSheetRef}
          index={1}
          snapPoints={snapPoints}
          backgroundStyle={[
            styles.bottomSheetBackground,
            { backgroundColor: theme.background },
          ]}
          handleIndicatorStyle={styles.bottomSheetHandle}
        >
          <BottomSheetScrollView
            contentContainerStyle={styles.bottomSheetContent}
          >
            {sheetContent}
          </BottomSheetScrollView>
        </BottomSheet>
      )}
    </GestureHandlerRootView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#f5f5f5",
  },
  loadingContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  loadingText: {
    marginTop: 10,
    fontSize: 16,
    color: "#666",
  },
  primaryButton: {
    backgroundColor: BrandColors.primary,
    paddingHorizontal: 32,
    paddingVertical: 14,
    borderRadius: 12,
    marginBottom: 12,
    width: "80%",
    alignItems: "center",
  },
  primaryButtonText: {
    color: "white",
    fontSize: 16,
    fontWeight: "600",
  },
  secondaryButton: {
    backgroundColor: "transparent",
    paddingHorizontal: 32,
    paddingVertical: 14,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: BrandColors.primary,
    width: "80%",
    alignItems: "center",
  },
  secondaryButtonText: {
    color: BrandColors.primary,
    fontSize: 16,
    fontWeight: "600",
  },
  mapContainer: {
    flex: 1,
    position: "relative",
  },
  backButton: {
    position: "absolute",
    left: 20,
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: "white",
    justifyContent: "center",
    alignItems: "center",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.15,
    shadowRadius: 4,
    elevation: 4,
    zIndex: 10,
  },
  backButtonText: {
    fontSize: 24,
    color: "#333",
    marginTop: -2,
  },
  statusBadge: {
    position: "absolute",
    right: 20,
    backgroundColor: "white",
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 20,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.15,
    shadowRadius: 4,
    elevation: 4,
    zIndex: 10,
  },
  statusDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: "#34C759",
    marginRight: 6,
  },
  statusText: {
    fontSize: 12,
    fontWeight: "600",
    color: "#333",
  },
  trackingBadge: {
    position: "absolute",
    right: 20,
    backgroundColor: "white",
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 20,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.15,
    shadowRadius: 4,
    elevation: 4,
    zIndex: 10,
  },
  trackingDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: "#FF3B30",
    marginRight: 8,
  },
  trackingActive: {
    backgroundColor: "#34C759",
  },
  trackingText: {
    fontSize: 12,
    fontWeight: "600",
    color: "#333",
  },
  bottomSheetBackground: {
    backgroundColor: "#0b1120",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: -2 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 10,
  },
  bottomSheetHandle: {
    backgroundColor: "#475569",
    width: 40,
  },
  bottomSheetContent: {
    padding: 24,
    paddingBottom: 40,
  },
  actionsSection: {
    marginBottom: 12,
  },
  actionButton: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    gap: 12,
  },
  actionButtonText: {
    fontSize: 16,
    color: "#fff",
    fontWeight: "600",
  },
  startRideButton: {
    backgroundColor: BrandColors.primary,
  },
  completeButton: {
    backgroundColor: BrandColors.success,
  },
  cancelButton: {
    backgroundColor: "#FF3B30",
  },
  inspectionButton: {
    backgroundColor: "#5856D6",
  },
  infoMessage: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#1e293b",
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    gap: 12,
  },
  infoMessageText: {
    flex: 1,
    fontSize: 14,
    color: "#94a3b8",
    lineHeight: 20,
  },
});
